import { useData } from '@/hooks/use-data';
import type { RootState } from '@/store';
import {
  selectActiveUserLocations,
  selectUserLocationById,
  selectUserLocationsStatus,
} from '@/store/selectors';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

/**
 * Custom hook for managing user locations
 * Provides easy access to user locations data and management functions
 */
export const useUserLocations = () => {
  const {
    userLocations,
    userLocationsLoading,
    userLocationsError,
    userLocationsFetched,
    setUserLocations,
    setUserLocationsLoading,
    setUserLocationsError,
  } = useData();

  // Selectors for computed data
  const locationsStatus = useSelector(selectUserLocationsStatus);
  const activeLocations = useSelector(selectActiveUserLocations);

  // Memoized selector creator for getting location by ID
  // Components should use this with useSelector: useSelector(getLocationByIdSelector(locationId))
  const getLocationByIdSelector = useCallback(
    (locationId: string) => (state: RootState) =>
      selectUserLocationById(state, locationId),
    []
  );

  return {
    // Data
    locations: userLocations,
    activeLocations,
    loading: userLocationsLoading,
    error: userLocationsError,
    fetched: userLocationsFetched,

    // Status
    status: locationsStatus,

    // Actions
    setLocations: setUserLocations,
    setLoading: setUserLocationsLoading,
    setError: setUserLocationsError,

    // Utilities
    getLocationByIdSelector,
    hasLocations: locationsStatus.hasLocations,
    locationsCount: locationsStatus.locationsCount,
    isReady: locationsStatus.isReady,
  };
};
